import React, { useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useConfig } from "@/context/ConfigContext";
import BREEDS from "@/data/dog_breeds.json";
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  Lock,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";

const MIXED_OTHER = "Mixed / Other";
const TEMPERAMENTS = [
  { v: "friendly", label: "Friendly" },
  { v: "shy", label: "Shy" },
  { v: "reactive", label: "Reactive" },
  { v: "unknown", label: "Unknown" },
];
const INSIDE_OUTSIDE = [
  { v: "inside", label: "Inside" },
  { v: "outside", label: "Outside" },
  { v: "both", label: "Both" },
];
const DURING_CLEANING = [
  { v: "inside_home", label: "Inside the home" },
  { v: "kenneled", label: "Kenneled" },
  { v: "other", label: "Other" },
];

const ABSOLUTE = (url) => `${process.env.REACT_APP_BACKEND_URL}${url}`;
const MAX_PHOTOS = 3;
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

// ---------- Breed combobox ----------
function BreedCombobox({ value, onChange }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  React.useEffect(() => {
    setQuery(value || "");
  }, [value]);

  const filtered = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    if (!q) return BREEDS.slice(0, 80);
    const matches = BREEDS.filter((b) => b.toLowerCase().includes(q));
    // Always include Mixed / Other at the end if not already present
    if (!matches.includes(MIXED_OTHER)) matches.push(MIXED_OTHER);
    return matches.slice(0, 80);
  }, [query]);

  const pick = (b) => {
    onChange?.(b);
    setQuery(b);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    setQuery("");
    onChange?.(null);
  };

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && filtered[highlight]) {
      e.preventDefault();
      pick(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={handleKey}
          placeholder="Type a breed…"
          autoComplete="off"
          className="h-11 rounded-full px-5 pr-16"
          data-testid="breed-combobox-input"
        />
        {query && (
          <button
            type="button"
            onMouseDown={clear}
            className="absolute right-9 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-pp-text"
            aria-label="Clear breed"
            data-testid="breed-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      </div>

      {open && (
        <ul
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-2xl border border-border bg-pp-surface py-1 shadow-md"
          data-testid="breed-options"
        >
          {filtered.map((b, i) => (
            <li
              key={b}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(b);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                "cursor-pointer px-4 py-2 text-sm",
                i === highlight ? "bg-brand-orange/10 text-pp-text" : "text-foreground",
                b === MIXED_OTHER && "border-t border-border font-semibold text-pp-text"
              )}
              data-testid={`breed-option-${i}`}
            >
              {b}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Pill toggle ----------
function PillToggle({ value, onChange, options, testIdPrefix }) {
  return (
    <div className="inline-flex rounded-full bg-secondary p-1" data-testid={`${testIdPrefix}-toggle`}>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(value === o.v ? null : o.v)}
          className={cn(
            "rounded-full px-4 py-1.5 text-sm font-semibold transition-all",
            value === o.v
              ? "bg-brand-orange text-white shadow-sm"
              : "text-pp-text hover:text-brand-orange"
          )}
          data-testid={`${testIdPrefix}-${o.v}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------- DropZone ----------
function DropZone({ onFiles, disabled }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) onFiles(files);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragEnter={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      className={cn(
        "flex min-h-[88px] flex-1 cursor-pointer items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-5 py-4 text-sm transition-colors",
        drag
          ? "border-brand-orange bg-brand-orange/15 text-brand-orange"
          : "border-border bg-muted/40 text-muted-foreground hover:border-brand-orange/60 hover:text-pp-text",
        disabled && "pointer-events-none opacity-50"
      )}
      data-testid="dog-photo-dropzone"
    >
      <Upload className="h-5 w-5" />
      <span>Drag photos here, or click to browse.</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          if (files.length) onFiles(files);
        }}
      />
    </div>
  );
}

// ---------- DogCard ----------
export default function DogCard({ dog, onUpdate, onDelete }) {
  const { pricing } = useConfig();
  const { requestAction, gateProps } = useImpersonationGate();
  const [uploading, setUploading] = useState(false);
  const [local, setLocal] = useState({
    name: dog.name || "",
    notes: dog.notes || "",
  });
  const notesTimer = useRef(null);

  const persistApi = async (patch) => {
    try {
      const { data } = await api.patch(`/profiles/customer/dogs/${dog.id}`, patch);
      onUpdate(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  // Gate-wrapped: every PATCH to a dog row is a "save dog changes"
  // confirmation when an admin is impersonating.
  const persist = (patch) => {
    requestAction("save dog changes", () => persistApi(patch));
  };

  const uploadOneApi = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API_BASE}/profiles/customer/dogs/${dog.id}/photos`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("pp_token")}` },
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, status: res.status, detail: data?.detail || "Upload failed" };
    onUpdate(data);
    return { ok: true };
  };

  const handleFiles = async (files) => {
    const remaining = MAX_PHOTOS - (dog.photos || []).length;
    if (remaining <= 0) {
      toast.error("Maximum 3 photos per dog. Remove one to upload another.");
      return;
    }
    const accepted = files.filter((f) => ALLOWED_MIME.includes(f.type) && f.size <= MAX_BYTES);
    if (accepted.length !== files.length) {
      toast.error("Some files were skipped (invalid type or > 5 MB).");
    }
    const toUpload = accepted.slice(0, remaining);
    const overflow = accepted.length - toUpload.length;
    if (overflow > 0) {
      toast.message(
        `Skipped ${overflow} — max 3 photos per dog.`
      );
    }
    // Gate the WHOLE batch as a single confirmation — keeps the UX
    // sensible when an admin drops 3 photos at once (1 modal, not 3).
    requestAction("upload dog photo", async () => {
      setUploading(true);
      for (const f of toUpload) {
        // eslint-disable-next-line no-await-in-loop
        const r = await uploadOneApi(f);
        if (!r.ok) {
          toast.error(r.detail);
          break;
        }
      }
      setUploading(false);
    });
  };

  const removePhotoApi = async (photo_url) => {
    try {
      const res = await fetch(`${API_BASE}/profiles/customer/dogs/${dog.id}/photos`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("pp_token")}`,
        },
        body: JSON.stringify({ photo_url }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || "Could not remove photo.");
        return;
      }
      onUpdate(data);
    } catch (e) {
      toast.error(e.message);
    }
  };
  const removePhoto = (photo_url) => {
    requestAction("remove dog photo", () => removePhotoApi(photo_url));
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${dog.name || "this dog"}?`)) return;
    requestAction("remove this dog", async () => {
      try {
        await api.delete(`/profiles/customer/dogs/${dog.id}`);
        onDelete(dog.id);
        toast.success("Dog removed.");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    });
  };

  const photos = dog.photos || [];
  const photoCount = photos.length;
  const isMaxed = photoCount >= MAX_PHOTOS;
  const securement = !!dog.securement_fee_applies;

  return (
    <div
      className="rounded-3xl border border-border bg-pp-surface p-6"
      data-testid={`dog-card-${dog.id}`}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-lg font-semibold text-pp-text">
          {dog.name || "Unnamed dog"}
        </h3>
        {securement && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-slate px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-orange"
            data-testid={`dog-securement-pill-${dog.id}`}
          >
            <Lock className="h-3.5 w-3.5" />
            Securement fee applies
          </span>
        )}
      </div>

      {/* Photos */}
      <div className="mb-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          {photos.map((url) => (
            <div
              key={url}
              className="group relative h-20 w-20 overflow-hidden rounded-2xl border border-border bg-muted"
              data-testid={`dog-photo-${dog.id}`}
            >
              <img src={ABSOLUTE(url)} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(url)}
                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-brand-slate/80 text-white opacity-0 transition-opacity hover:bg-brand-slate group-hover:opacity-100"
                aria-label="Remove photo"
                data-testid={`dog-photo-remove-${dog.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {uploading && (
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-orange/10">
              <Loader2 className="h-5 w-5 animate-spin text-brand-orange" />
            </div>
          )}
        </div>
        {isMaxed ? (
          <p className="text-xs font-semibold text-pp-text" data-testid={`dog-photo-count-${dog.id}`}>
            {photoCount} / {MAX_PHOTOS} photos
          </p>
        ) : (
          <DropZone onFiles={handleFiles} disabled={uploading} />
        )}
      </div>

      {/* Fields */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-sm font-semibold text-pp-text">Name</Label>
          <Input
            value={local.name}
            onChange={(e) => setLocal((s) => ({ ...s, name: e.target.value }))}
            onBlur={() => local.name !== (dog.name || "") && persist({ name: local.name })}
            placeholder="Rex"
            className="mt-2 h-11 rounded-full px-5"
            data-testid={`dog-name-input-${dog.id}`}
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-pp-text">Breed</Label>
          <div className="mt-2">
            <BreedCombobox
              value={dog.breed}
              onChange={(b) => persist({ breed: b })}
            />
          </div>
          {dog.breed === MIXED_OTHER && (
            <Input
              value={dog.breed_other || ""}
              onChange={(e) =>
                onUpdate({ ...dog, breed_other: e.target.value })
              }
              onBlur={(e) => persist({ breed_other: e.target.value || null })}
              placeholder="Describe the mix (optional)"
              className="mt-2 h-11 rounded-full px-5"
              data-testid={`dog-breed-other-input-${dog.id}`}
            />
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-sm font-semibold text-pp-text">Temperament</Label>
          <div className="mt-2">
            <PillToggle
              value={dog.temperament || "unknown"}
              onChange={(v) => persist({ temperament: v || "unknown" })}
              options={TEMPERAMENTS}
              testIdPrefix={`dog-temperament-${dog.id}`}
            />
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-pp-text">Inside or outside?</Label>
          <div className="mt-2">
            <PillToggle
              value={dog.is_inside_or_outside}
              onChange={(v) => persist({ is_inside_or_outside: v })}
              options={INSIDE_OUTSIDE}
              testIdPrefix={`dog-inout-${dog.id}`}
            />
          </div>
        </div>
      </div>

      {/* During cleaning */}
      <div className="mt-5">
        <Label className="text-sm font-semibold text-pp-text">
          During cleaning, the dog will be:
        </Label>
        <div
          className="mt-2 flex flex-wrap gap-2"
          role="radiogroup"
          data-testid={`dog-during-cleaning-${dog.id}`}
        >
          {DURING_CLEANING.map((o) => {
            const active = dog.during_cleaning === o.v;
            return (
              <button
                key={o.v}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => persist({ during_cleaning: o.v })}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm font-semibold transition-all",
                  active
                    ? "border-brand-orange bg-brand-orange text-white"
                    : "border-border bg-pp-surface text-pp-text hover:border-brand-orange/40 hover:text-brand-orange"
                )}
                data-testid={`dog-during-cleaning-${dog.id}-${o.v}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>

        {dog.during_cleaning === "other" && (
          <div
            className="mt-3 flex items-start gap-3 rounded-2xl border border-brand-orange/40 bg-brand-orange/10 p-4"
            data-testid={`dog-securement-warning-${dog.id}`}
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
            <p className="text-sm text-pp-text">
              Your dog won&apos;t be secured during service. A securement fee of{" "}
              <span className="font-semibold text-pp-text">
                ${Number(pricing.securement_fee || 0).toFixed(2)}
              </span>{" "}
              will apply per visit so our tech can work safely without risk of escape.
            </p>
          </div>
        )}
      </div>

      <div className="mt-5">
        <Label className="text-sm font-semibold text-pp-text">
          Anything we should know?
        </Label>
        <Textarea
          value={local.notes}
          onChange={(e) => {
            const v = e.target.value;
            setLocal((s) => ({ ...s, notes: v }));
            if (notesTimer.current) clearTimeout(notesTimer.current);
            notesTimer.current = setTimeout(() => {
              if (v !== (dog.notes || "")) persist({ notes: v });
            }, 500);
          }}
          rows={2}
          placeholder="Gate latch is finicky; loves treats."
          className="mt-2 rounded-2xl px-5 py-3"
          data-testid={`dog-notes-input-${dog.id}`}
        />
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          type="button"
          variant="ghost"
          onClick={handleDelete}
          className="rounded-full text-destructive hover:bg-destructive/10"
          data-testid={`dog-delete-btn-${dog.id}`}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Remove dog
        </Button>
      </div>
      <ImpersonationConfirmGate {...gateProps} />
    </div>
  );
}
